import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useUpdateInvoiceRowsMutation } from 'store/api/invoices';
import { ClassifierData } from 'types/classifiers';
import {
    EditIntentTypeCode,
    InvoiceData,
    InvoiceDetailsTotals,
    InvoiceRowBulkUpdateDto,
    InvoiceRowCorrectionForm,
    InvoiceRowCorrectionFormRow,
    InvoiceRowForm,
    InvoiceType,
} from 'types/invoices';
import InvoiceRowActions from '../invoice-rows/InvoiceRowActions';
import InvoiceTotalsTableHeader from '../InvoiceTotalsTableHeader';
import { TotalsTableCard } from '../TotalsTableCard';
import { AdminFeeAddButton } from './AdminFeeAddButton';
import AdminFeeTotalsTableItem from './AdminFeeTotalsTableItem';

interface Props {
    invoiceTotals?: InvoiceDetailsTotals;
    invoice: InvoiceData;
    isInViewMode: boolean;
    matterId: number;
    handleAddRow: (data: InvoiceRowForm) => void;
    paymentCurrency: string;
    baseAmount: number;
    defaultAdminFeeRate?: number;
    classifiers: Record<string, ClassifierData>;
}

const AdminFeeTotals = ({
    invoiceTotals,
    invoice,
    isInViewMode,
    matterId,
    handleAddRow,
    paymentCurrency,
    baseAmount,
    defaultAdminFeeRate,
    classifiers,
}: Props) => {
    const isCorrectionInvoice = invoice.invoiceTypeCode === InvoiceType.CORRECTION;
    // A shared-payment copy cannot take a fee of its own: its amounts are the group's.
    const { isSharedPaymentCopy } = useSharedPayment(invoice);
    const [updateInvoiceRows] = useUpdateInvoiceRowsMutation();

    const adminFeeRows = useMemo(
        () => (invoiceTotals?.invoiceRowTotals ?? []).filter((row) => row.invoiceRowTypeCode === 'ADMIN_FEE'),
        [invoiceTotals?.invoiceRowTotals]
    );

    const formValues: InvoiceRowCorrectionForm = useMemo(
        () => ({
            rows: adminFeeRows.map((row) => ({
                id: row.invoiceRowId,
                selected: false,
                editIntentTypeCode: row.editIntentTypeCode,
            })),
        }),
        [adminFeeRows]
    );

    const { control, getValues, setValue } = useForm<InvoiceRowCorrectionForm>({
        values: formValues,
    });

    const { fields, remove } = useFieldArray({
        control,
        name: 'rows',
        keyName: 'formKey',
    });

    const handleUpdateInvoiceRows = useCallback(
        async (idList: number[]) => {
            const dtoList: InvoiceRowBulkUpdateDto[] = [];
            const rows: InvoiceRowCorrectionFormRow[] = getValues('rows') || [];
            rows.filter((row) => idList.includes(row.id)).forEach((row) =>
                dtoList.push({
                    id: row.id,
                    editIntentTypeCode: row.editIntentTypeCode,
                })
            );

            await updateInvoiceRows({ invoiceId: invoice.id, matterId, dtoList }).unwrap().catch(console.error);
        },
        [getValues, invoice.id, matterId, updateInvoiceRows]
    );

    const handleCorrect = useCallback(() => {
        const rows: InvoiceRowCorrectionFormRow[] = getValues('rows') || [];
        const idList: number[] = [];
        const updatedRows = rows.map((row) => {
            if (row.id && row.selected) {
                idList.push(row.id);
                return {
                    ...row,
                    selected: false,
                    editIntentTypeCode: EditIntentTypeCode.REMOVE_VIA_CORRECTION,
                };
            }
            return row;
        });
        setValue('rows', updatedRows, { shouldDirty: true });
        handleUpdateInvoiceRows(idList);
    }, [getValues, handleUpdateInvoiceRows, setValue]);

    const tableContent = useMemo(
        () =>
            fields.map((field, fieldIndex) => {
                const backendRow = adminFeeRows.find((row) => row.invoiceRowId === field.id);
                return (
                    <AdminFeeTotalsTableItem
                        key={field.formKey}
                        index={fieldIndex}
                        matterId={matterId}
                        invoiceRowTotals={backendRow}
                        isInViewMode={isInViewMode}
                        invoice={invoice}
                        remove={remove}
                        control={control}
                        setValue={setValue}
                        handleUpdateInvoiceRows={(idList) => {
                            void handleUpdateInvoiceRows(idList);
                        }}
                        baseAmount={baseAmount}
                        classifiers={classifiers}
                    />
                );
            }),
        [
            fields,
            matterId,
            adminFeeRows,
            isInViewMode,
            invoice,
            remove,
            control,
            setValue,
            handleUpdateInvoiceRows,
            baseAmount,
            classifiers,
        ]
    );

    return (
        <TotalsTableCard
            sticky
            title={<FormattedMessage id="invoices.totals-tab.admin-fees" />}
            isEmpty={!fields.length}
            actions={
                <div className="flex flex-row items-center gap-2">
                    {!isInViewMode && !isSharedPaymentCopy && fields.length === 0 ? (
                        <AdminFeeAddButton
                            invoice={invoice}
                            handleSubmit={handleAddRow}
                            matterId={matterId}
                            baseAmount={baseAmount}
                            defaultAdminFeeRate={defaultAdminFeeRate}
                            classifiers={classifiers}
                        >
                            <FormattedMessage id="invoices.totals-tab.add-admin-fee" />
                        </AdminFeeAddButton>
                    ) : null}
                    {!isInViewMode && isCorrectionInvoice && fields.length ? (
                        <InvoiceRowActions handleCorrect={handleCorrect} control={control} />
                    ) : null}
                </div>
            }
        >
            <InvoiceTotalsTableHeader
                paymentCurrency={paymentCurrency}
                discountHeaderId="invoices.totals-tab.table-headings.percentage"
            />
            <div className="flex flex-col">{tableContent}</div>
        </TotalsTableCard>
    );
};

export default AdminFeeTotals;
